import {
  MAX_GAMES_PER_DAY,
  ruleGuardListSchema,
  type GuardEffect,
  type RuleGuard,
  type Weekday,
} from '@gamble-chess/contracts';

export type GuardKind = RuleGuard['kind'];

/** What the guard fields hold while the user fills them in; every kind's fields at once. */
export interface RuleGuardDraft {
  readonly kind: GuardKind;
  readonly effect: GuardEffect;
  readonly days: readonly Weekday[];
  readonly start: string;
  readonly end: string;
  readonly from: string;
  readonly to: string;
  readonly gamesPerDay: string;
}

/** Today where the browser is, which is the day a date range most often starts on. */
function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${String(now.getFullYear())}-${month}-${String(now.getDate()).padStart(2, '0')}`;
}

export function initialRuleGuardDraft(): RuleGuardDraft {
  const date = today();
  return {
    kind: 'weekly-window',
    effect: 'block',
    days: ['mon', 'tue', 'wed', 'thu', 'fri'],
    start: '09:00',
    end: '17:00',
    from: date,
    to: date,
    gamesPerDay: '3',
  };
}

function candidateOf(draft: RuleGuardDraft): unknown {
  switch (draft.kind) {
    case 'weekly-window':
      return {
        kind: draft.kind,
        effect: draft.effect,
        days: draft.days,
        start: draft.start,
        end: draft.end,
      };
    case 'date-range':
      return { kind: draft.kind, effect: draft.effect, from: draft.from, to: draft.to };
    case 'daily-cap':
      return { kind: draft.kind, effect: draft.effect, gamesPerDay: Number(draft.gamesPerDay) };
  }
}

function explain(draft: RuleGuardDraft): string {
  switch (draft.kind) {
    case 'weekly-window':
      return draft.days.length === 0
        ? 'Pick at least one day the window covers.'
        : 'The window must not end before it starts.';
    case 'date-range':
      return 'The range must not end before it starts.';
    case 'daily-cap':
      return `Games a day must be a whole number from 1 to ${String(MAX_GAMES_PER_DAY)}.`;
  }
}

export type ParsedRuleGuard =
  { readonly ok: true; readonly guard: RuleGuard } | { readonly ok: false; readonly error: string };

/** Checks the fields against the same contract the server applies, ordering included. */
export function parseRuleGuard(draft: RuleGuardDraft): ParsedRuleGuard {
  const parsed = ruleGuardListSchema.safeParse([candidateOf(draft)]);
  const guard = parsed.success ? parsed.data[0] : undefined;
  return guard === undefined ? { ok: false, error: explain(draft) } : { ok: true, guard };
}
